import React from 'react';
import { LandingLayout } from 'components/Landing/LandingLayout';
import useMobxStores from 'hooks/useMobxStores';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import { handleError } from 'services/ErrorService';
import ComponentError from 'components/Error/ComponentError';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-mobx-router5';
import DurationIcon from 'img/icons/estimated-duration.svg';
import ExerciseIcon from 'img/icons/level.svg';
import PagesIcon from 'img/icons/paper-stack.svg';
import VideoIcon from 'img/icons/video.svg';
import LabIcon from 'img/icons/lab-2.svg';
import { LabGradeResults } from 'types/labs';

interface SidebarDetailsProps {
  audienceLabels?: string[];
  certifications?: string[];
  labGradeResults?: LabGradeResults;
  language: string;

  /** When provided, called on hover over certification links (code or null on leave) */
  onCertificationHover?: (code: string | null) => void;

  productLabels?: string[];
  topicLabels?: string[];
  totalDuration?: number;
  totalGuidedExercises?: number;
  totalLabs?: number;
  totalQuiz?: number;
  totalVideos?: number;
}

const SidebarDetails: React.FC<SidebarDetailsProps> = ({
  audienceLabels,
  certifications,
  labGradeResults,
  language,
  onCertificationHover,
  productLabels,
  topicLabels,
  totalDuration,
  totalGuidedExercises,
  totalLabs,
  totalQuiz,
  totalVideos,
}) => {
  const { t } = useTranslation();
  const { catalogStore } = useMobxStores();
  const hasDuration = (totalDuration ?? 0) > 0;
  const hasGuidedExercises = (totalGuidedExercises ?? 0) > 0;
  const hasQuiz = (totalQuiz ?? 0) > 0;
  const hasVideos = (totalVideos ?? 0) > 0;
  const hasProductLabels = (productLabels?.length ?? 0) > 0;
  const hasAudienceLabels = (audienceLabels?.length ?? 0) > 0;
  const hasTopicLabels = (topicLabels?.length ?? 0) > 0;
  const hasCertifications = (certifications?.length ?? 0) > 0;
  const hasLabs = (totalLabs ?? 0) > 0;
  const hasLabGradeResults = !!labGradeResults && labGradeResults?.total > 0;

  const labGradeChips: {
    badgeValue: string;
    children: React.ReactNode;
    color?: 'green' | 'gray' | 'yellow';
    dataAnalyticsId?: string;
    tooltipContent?: string;
  }[] = [];

  if (hasLabGradeResults && labGradeResults) {
    if (labGradeResults.passed !== labGradeResults.total) {
      labGradeChips.push({
        badgeValue: `${labGradeResults.graded}/${labGradeResults.total}`,
        children: t('Graded'),
        color: labGradeResults.graded === 0 ? 'gray' : 'yellow',
        dataAnalyticsId: 'lab-grade-chip-graded',
        tooltipContent: t(
          'The number of labs where the lab grade script was run, out of the total labs that include a lab grade script.',
        ),
      });
    }

    labGradeChips.push({
      badgeValue: `${labGradeResults.passed}/${labGradeResults.total}`,
      children: t('Passed'),
      color: 'green',
      dataAnalyticsId: 'lab-grade-chip-passed',
      tooltipContent: t(
        'The number of labs that earned 100% passing objectives, out of the total labs that include a lab grade script.',
      ),
    });
  }

  const hasAnyMetadata =
    hasDuration ||
    hasGuidedExercises ||
    hasQuiz ||
    hasVideos ||
    hasProductLabels ||
    hasAudienceLabels ||
    hasTopicLabels ||
    hasCertifications ||
    hasLabs;

  const renderLabelList = (label: string, items?: string[]) =>
    items?.length ? (
      <LandingLayout.MetadataText>
        {t(label)}: <span>{items.filter(Boolean).join(', ')}</span>
      </LandingLayout.MetadataText>
    ) : null;

  return (
    <LandingLayout.Metadata>
      {!hasAnyMetadata ? (
        <LandingLayout.MetadataText>
          {t('No additional details available for this offering.')}
        </LandingLayout.MetadataText>
      ) : (
        <>
          {hasDuration && (
            <LandingLayout.MetadataWithIcon iconSrc={DurationIcon}>
              <LandingLayout.MetadataText>
                {t('Estimated duration')}
                {': '}
                <span>{`${Math.ceil(totalDuration / 60)}h`}</span>
              </LandingLayout.MetadataText>
            </LandingLayout.MetadataWithIcon>
          )}
          {hasGuidedExercises && (
            <LandingLayout.MetadataWithIcon iconSrc={ExerciseIcon}>
              <LandingLayout.MetadataText>
                {t('Guided exercises')}: <span>{totalGuidedExercises}</span>
              </LandingLayout.MetadataText>
            </LandingLayout.MetadataWithIcon>
          )}
          {hasQuiz && (
            <LandingLayout.MetadataWithIcon iconSrc={PagesIcon}>
              <LandingLayout.MetadataText>
                {t('Quizzes')}: <span>{totalQuiz}</span>
              </LandingLayout.MetadataText>
            </LandingLayout.MetadataWithIcon>
          )}
          {hasVideos && (
            <LandingLayout.MetadataWithIcon iconSrc={VideoIcon}>
              <LandingLayout.MetadataText>
                {t('Videos')}: <span>{totalVideos}</span>
              </LandingLayout.MetadataText>
            </LandingLayout.MetadataWithIcon>
          )}
          {hasLabs && (
            <>
              <LandingLayout.MetadataWithIcon iconSrc={LabIcon}>
                <LandingLayout.MetadataText>
                  {t('Labs')}: {!hasLabGradeResults && <span>{totalLabs}</span>}
                </LandingLayout.MetadataText>
              </LandingLayout.MetadataWithIcon>

              {hasLabGradeResults && (
                <LandingLayout.MetadataChips chips={labGradeChips} />
              )}
            </>
          )}
          {renderLabelList(t('Products'), productLabels)}
          {renderLabelList(t('Audiences'), audienceLabels)}
          {renderLabelList(t('Topics'), topicLabels)}
          {hasCertifications && (
            <LandingLayout.MetadataText>
              {t('Associated certifications')}:{' '}
              <span>
                {certifications.map((c, index) => (
                  <React.Fragment key={c}>
                    {c !== '' && (
                      <>
                        <span
                          onMouseEnter={() =>
                            onCertificationHover?.(c.toLowerCase())
                          }
                          onMouseLeave={() => onCertificationHover?.(null)}
                        >
                          <Link
                            href
                            routeName="exams:view"
                            routeParams={{
                              slug: c.toLowerCase(),
                            }}
                          >
                            {catalogStore.getTranslatedCourseName(
                              c.toLowerCase(),
                              language,
                            )}
                          </Link>
                        </span>
                        {index !== certifications.length - 1 && <span>, </span>}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </span>
            </LandingLayout.MetadataText>
          )}
        </>
      )}
    </LandingLayout.Metadata>
  );
};

export default withErrorBoundary(
  observer(SidebarDetails),
  ComponentError,
  handleError,
);
